import { useId, useMemo } from "react";
import { FormControlContext } from "./FormControlContext";
import type { FormControlProps } from "./FormControlProps";
import classes from "./FormControl.module.scss";
import { clsx } from "clsx";
import { Required } from "@/components/ui/Icon/Required";

export function FormControl(props: FormControlProps) {
  const {
    htmlFor: htmlForProp,
    label,
    required,
    helperText,
    children,
    error,
    className,
    isValid,
    autoFit,
    type = "default",
  } = props;
  const defaultId = useId();

  const id = htmlForProp ?? defaultId;
  const errorId = `${id}-error`;

  const isFieldset = type === "fieldset";
  const Root = isFieldset ? "fieldset" : "div";
  const legendId = isFieldset && label ? `${id}-legend` : undefined;
  const hasError = !!error;

  const contextValue = useMemo(
    () => ({
      id,
      errorId,
      legendId,
      hasError,
      isValid: isValid ?? undefined,
    }),
    [id, errorId, legendId, hasError, isValid],
  );

  return (
    <FormControlContext.Provider value={contextValue}>
      <Root role="group" className={clsx(classes.root, className)}>
        {label && (
          <>
            {isFieldset ? (
              <legend id={legendId} className={classes.label}>
                {label}
                {required && (
                  <Required
                    size={10}
                    className={classes.required}
                    aria-label="required"
                  />
                )}
                {required === false && (
                  <span className={classes.optional}>(optional)</span>
                )}
              </legend>
            ) : (
              <label htmlFor={id} className={classes.label}>
                {label}
                {required && (
                  <Required
                    size={10}
                    className={classes.required}
                    aria-label="required"
                  />
                )}
                {required === false && (
                  <>
                    {" "}
                    <span className={classes.optional}>(optional)</span>
                  </>
                )}
              </label>
            )}
          </>
        )}

        <div className={clsx(classes.inputWrapper, autoFit && classes.autoFit)}>
          {children}
        </div>

        {!error && helperText && (
          <div className={clsx(classes.helperText, classes.smallText)}>
            {helperText}
          </div>
        )}

        {/* The live region stays mounted: a screen reader ignores a region that
            is inserted together with its text. Its classes come and go with the
            message so an empty region takes no vertical space. */}
        <div
          id={errorId}
          aria-live="polite"
          className={error ? clsx(classes.error, classes.smallText) : undefined}
        >
          {error}
        </div>
      </Root>
    </FormControlContext.Provider>
  );
}
